import { Stack, useLocalSearchParams } from 'expo-router';
import { ScrollView } from 'react-native';
import { useQuery } from 'urql';
import Avatar from '@/components/Custom/Avatar';
import * as Header from '@/components/Custom/Header';
import { isRichTextEmpty } from '@/components/RichText/RichText.utils';
import RichTextDisplay from '@/components/RichText/RichTextDisplay';
import Box from '@/components/UI/Box';
import Card from '@/components/UI/Card';
import ContentBoundary from '@/components/UI/ContentBoundary';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';

const GetEventRsvpNoteDetail = graphql(/* GraphQL */ `
  query GetEventRsvpNoteDetail($eventId: uuid!, $personId: String!) {
    person_by_pk(id: $personId) {
      id
      avatar_id
      color
      name
      event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
        event_id
        person_id
        notes
      }
    }
  }
`);

export default function EventRsvpNoteDetail() {
  const { eventId, personId } = useLocalSearchParams<{
    eventId?: string;
    personId?: string;
  }>();
  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetEventRsvpNoteDetail,
    variables: { eventId: eventId ?? '', personId: personId ?? '' },
    pause: !eventId || !personId,
  });
  const person = data?.person_by_pk;
  const notes = person?.event_rsvp[0]?.notes ?? '';
  const hasNotes = !isRichTextEmpty(notes);
  const headerTitle = () => (
    <Header.TitleContainer testID="EventRsvpNoteDetailHeader">
      <Avatar
        color={person?.color ?? ''}
        id={person?.avatar_id ?? ''}
        name={person?.name ?? ''}
        size="md"
      />
      <Box flex>
        <Text flexShrink={1} numberOfLines={1} size="xl">
          {person?.name ?? 'RSVP Note'}
        </Text>
        <Box alignItems="center" direction="horizontal" space="sm">
          <Icon
            name="document-text-outline"
            size="sm"
            variant="typographySecondary"
          />
          <Text numberOfLines={1} size="xs" variant="typographySecondary">
            RSVP Note
          </Text>
        </Box>
      </Box>
    </Header.TitleContainer>
  );

  return (
    <Box flex testID="EventRsvpNoteDetail">
      <Stack.Screen
        options={{
          headerTitle,
          title: '',
        }}
      />
      <ScrollView contentInsetAdjustmentBehavior="automatic">
        <Box p="md" testID="EventRsvpNoteDetailContent">
          <Card>
            <Box p="md">
              <ContentBoundary
                data={data}
                loading={fetching}
                error={error}
                flex={false}
                onRetry={() => refresh({ requestPolicy: 'network-only' })}
                errorMessage="Could not load this RSVP note."
              >
                {() =>
                  hasNotes ? (
                    <RichTextDisplay html={notes} />
                  ) : (
                    <Text variant="typographySecondary">
                      This RSVP note is no longer available.
                    </Text>
                  )
                }
              </ContentBoundary>
            </Box>
          </Card>
        </Box>
      </ScrollView>
    </Box>
  );
}
